// Separate intentional steady/rising movement from a decaying momentum tail.
// Physical wheel notches can remain equal for a whole gesture; requiring an idle
// gap or a 2x rebound would otherwise lock them out indefinitely.
export function createReaderWheelGate({ threshold = 24, idleMs = 90, rearmMs = 120, notchGapMs = 40 } = {}) {
  let direction = 0;
  let distance = 0;
  let lastAt = null;
  let lastTurnAt = null;
  let lastMagnitude = 0;
  let momentum = false;
  let momentumFloor = 0;
  let locked = false;
  const reset = () => {
    direction = 0;
    distance = 0;
    lastAt = null;
    lastTurnAt = null;
    lastMagnitude = 0;
    momentum = false;
    momentumFloor = 0;
    locked = false;
  };
  return {
    reset,
    consume(deltaY, now = Date.now()) {
      if (!Number.isFinite(deltaY) || deltaY === 0 || !Number.isFinite(now)) return 0;
      const nextDirection = Math.sign(deltaY);
      const magnitude = Math.abs(deltaY);
      const gap = lastAt === null ? Infinity : now - lastAt;
      if (lastAt === null || gap >= idleMs || gap < 0 || nextDirection !== direction) {
        distance = 0;
        momentum = false;
        momentumFloor = 0;
        locked = false;
      }
      direction = nextDirection;
      lastAt = now;
      if (locked) {
        // Ignore insignificant floating-point noise, but retain even a slow
        // monotonic decay as momentum rather than treating it as a plateau.
        const epsilon = Math.max(0.001, lastMagnitude * 0.001);
        const falling = magnitude < lastMagnitude - epsilon;
        const steady = Math.abs(magnitude - lastMagnitude) <= epsilon;
        if (falling) {
          momentum = true;
          momentumFloor = momentumFloor ? Math.min(momentumFloor, magnitude) : magnitude;
          distance = 0;
        } else if (momentum && magnitude >= momentumFloor * 1.2 && magnitude - momentumFloor >= 0.5) {
          momentum = false;
          momentumFloor = 0;
          distance = magnitude;
        } else if (!momentum) {
          distance += magnitude;
        }
        const discreteNotch = !momentum && steady && magnitude >= threshold && gap >= notchGapMs;
        const activeMovement = !momentum && now - lastTurnAt >= rearmMs && distance >= threshold;
        lastMagnitude = magnitude;
        if (!discreteNotch && !activeMovement) return 0;
      } else {
        lastMagnitude = magnitude;
        distance += magnitude;
        if (distance < threshold) return 0;
      }
      locked = true;
      lastTurnAt = now;
      distance = 0;
      momentum = false;
      momentumFloor = 0;
      return direction;
    },
  };
}

// Let the browser scroll zoomed paper or nested content before turning a page.
export function findScrollableReaderParent(target, stopAt, direction) {
  if (!Number.isFinite(direction) || direction === 0 || !stopAt) return null;
  let element = target?.nodeType === 1 ? target : target?.parentElement;
  if (!element || !stopAt.contains(element)) return null;
  while (element) {
    const maxTop = element.scrollHeight - element.clientHeight;
    if (maxTop > 2) {
      const style = element.ownerDocument.defaultView.getComputedStyle(element);
      const overflowY = style.overflowY || style.overflow;
      const canMove = direction > 0 ? element.scrollTop < maxTop - 2 : element.scrollTop > 2;
      if (/^(auto|scroll)$/.test(overflowY) && canMove) return element;
    }
    if (element === stopAt) break;
    element = element.parentElement;
  }
  return null;
}
